import { Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { BrandingProvider } from './contexts/BrandingContext'
import { MaintenanceProvider } from './contexts/MaintenanceContext'
import { EmulationProvider } from './contexts/EmulationContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { queryClient } from './lib/react-query'
import { lazyWithReload } from './utils/lazyWithReload'
import Layout from './components/Layout'
import HelpLayout from './components/HelpLayout'
import PrivateRoute from './components/PrivateRoute'
import InactivityLogoutWrapper from './components/InactivityLogoutWrapper'
import ChunkErrorBoundary from './components/ChunkErrorBoundary'
import VersionUpdateBanner from './components/VersionUpdateBanner'

// Eager-loaded pages (small, frequently accessed)
import Dashboard from './pages/Dashboard'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import VerifyMfaPage from './pages/VerifyMfaPage'
import MfaRequiredPage from './pages/MfaRequiredPage'
import LogoutPage from './pages/LogoutPage'
import SsoCallbackPage from './pages/SsoCallbackPage'
import DemoSignupPage from './pages/DemoSignupPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import ConfirmEmailChangePage from './pages/ConfirmEmailChangePage'
import NotFoundPage from './pages/NotFoundPage'
import ForbiddenPage from './pages/ForbiddenPage'
import AcceptInvitation from './pages/AcceptInvitation/AcceptInvitation'

// Lazy-loaded pages (heavy or less frequently accessed)
// Form.io components are large (~1MB) - lazy load them
const FormBuilderPage = lazyWithReload(() => import('./pages/FormBuilderPage'))
const FormViewerPage = lazyWithReload(() => import('./pages/FormViewerPage'))
const CollaborativeSubmissionPage = lazyWithReload(() => import('./pages/CollaborativeSubmissionPage'))
const SignSubmissionPage = lazyWithReload(() => import('./pages/SignSubmissionPage/SignSubmissionPage'))
const SubmissionsPage = lazyWithReload(() => import('./pages/SubmissionsPage'))

// Admin pages - not needed on initial load
const UsersPage = lazyWithReload(() => import('./pages/UsersPage'))
const TenantsPage = lazyWithReload(() => import('./pages/TenantsPage'))
const SuperAdminTenantSettingsPage = lazyWithReload(() => import('./pages/SuperAdminTenantSettingsPage'))
const TenantSettingsPage = lazyWithReload(() => import('./pages/TenantSettingsPage'))
const PlatformUsersPage = lazyWithReload(() => import('./pages/PlatformUsersPage'))
const MyTasksPage = lazyWithReload(() => import('./pages/MyTasksPage'))
const MySubmissionsPage = lazyWithReload(() => import('./pages/MySubmissionsPage'))
const FormsPage = lazyWithReload(() => import('./pages/FormsPage'))
const AdminStatsPage = lazyWithReload(() => import('./pages/AdminStatsPage'))
const UsageTrackingPage = lazyWithReload(() => import('./pages/UsageTrackingPage'))
const AnalyticsDashboardPage = lazyWithReload(() => import('./pages/AnalyticsDashboardPage'))
const CreditConfigurationPage = lazyWithReload(() => import('./pages/CreditConfigurationPage'))
const EmulationAuditPage = lazyWithReload(() => import('./pages/EmulationAuditPage'))
const TenantAdministrationPage = lazyWithReload(() => import('./pages/TenantAdministrationPage'))
const PlatformSettingsPage = lazyWithReload(() => import('./pages/PlatformSettingsPage'))
const SupportTicketsPage = lazyWithReload(() => import('./pages/SupportTicketsPage'))
const BillingPage = lazyWithReload(() => import('./pages/BillingPage'))

// Import wizard - separate feature
const ImportWizardPage = lazyWithReload(() => import('./pages/ImportWizardPage'))
const ImportHistoryPage = lazyWithReload(() => import('./pages/ImportHistoryPage'))
const FixFailedRecordsPage = lazyWithReload(() => import('./pages/FixFailedRecordsPage'))

// Help pages - markdown content
const HelpPage = lazyWithReload(() => import('./pages/HelpPage'))

// Loading fallback component
function PageLoader() {
  return (
    <div className="loading-container">
      <div className="spinner"></div>
      <p className="loading-message">Loading...</p>
    </div>
  )
}

function HomeRedirect() {
  const { user, loading } = useAuth()

  // Wait for auth check to complete before redirecting
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <p>Loading...</p>
      </div>
    )
  }

  return <Navigate to={user ? "/dashboard" : "/login"} replace />
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrandingProvider>
        <MaintenanceProvider>
          <EmulationProvider>
            <LanguageProvider>
              <InactivityLogoutWrapper>
              <BrowserRouter>
                <VersionUpdateBanner />
                <ChunkErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
              {/* Default route redirects based on auth status */}
              <Route path="/" element={<HomeRedirect />} />

              {/* Public routes without layout */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/set-password" element={<ForgotPasswordPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/verify-mfa" element={<VerifyMfaPage />} />
              <Route path="/mfa-required" element={<PrivateRoute skipMfaCheck={true}><MfaRequiredPage /></PrivateRoute>} />
              <Route path="/logout" element={<LogoutPage />} />
              <Route path="/auth/callback" element={<SsoCallbackPage />} />
              <Route path="/f/:formId" element={<FormViewerPage />} />
              <Route path="/forms/:formId/view" element={<FormViewerPage />} />
              <Route path="/help" element={<HelpLayout><HelpPage /></HelpLayout>} />
              <Route path="/help/:section/:topic" element={<HelpLayout><HelpPage /></HelpLayout>} />
              <Route path="/sign-up" element={<DemoSignupPage />} />
              <Route path="/get-started" element={<DemoSignupPage getStarted />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              {/* Email-change confirmation landing (#312) — public, tokenized link. */}
              <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
              {/* Task 17 (#49): public accept-invite landing — no auth required. */}
              <Route path="/invitations/accept" element={<AcceptInvitation />} />
              {/* #652 — remote signing link landing. requestId + secret are read
                  from the URL fragment inside the page, never a route param. */}
              <Route path="/sign" element={<SignSubmissionPage />} />

              {/* Protected routes with layout */}
              <Route path="/dashboard" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
              <Route path="/forms" element={<PrivateRoute><Layout><FormsPage /></Layout></PrivateRoute>} />
              <Route path="/forms/new" element={<PrivateRoute><Layout><FormBuilderPage key="new-form" /></Layout></PrivateRoute>} />
              <Route path="/forms/:formId/edit" element={<PrivateRoute><Layout><FormBuilderPage /></Layout></PrivateRoute>} />
              <Route path="/forms/:formId/submissions" element={<PrivateRoute><Layout><SubmissionsPage /></Layout></PrivateRoute>} />
              <Route path="/forms/:formId/submissions/:submissionId" element={<PrivateRoute><Layout><SubmissionsPage /></Layout></PrivateRoute>} />
              <Route path="/users" element={<PrivateRoute><Layout><UsersPage /></Layout></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><Layout><TenantSettingsPage /></Layout></PrivateRoute>} />
              <Route path="/settings/api-keys" element={<Navigate to="/administration?tab=api-keys" replace />} />
              <Route path="/usage" element={<PrivateRoute><Layout><UsageTrackingPage /></Layout></PrivateRoute>} />
              <Route path="/analytics" element={<PrivateRoute><Layout><AnalyticsDashboardPage /></Layout></PrivateRoute>} />
              <Route path="/platform-users" element={<PrivateRoute><Layout><PlatformUsersPage /></Layout></PrivateRoute>} />
              <Route path="/tenants" element={<PrivateRoute><Layout><TenantsPage /></Layout></PrivateRoute>} />
              <Route path="/tenants/:tenantId/settings" element={<PrivateRoute><Layout><SuperAdminTenantSettingsPage /></Layout></PrivateRoute>} />
              <Route path="/my-tasks" element={<PrivateRoute><Layout><MyTasksPage /></Layout></PrivateRoute>} />
              <Route path="/my-submissions" element={<PrivateRoute><Layout><MySubmissionsPage /></Layout></PrivateRoute>} />
              {/* Admin routes */}
              <Route path="/admin/stats" element={<PrivateRoute><Layout><AdminStatsPage /></Layout></PrivateRoute>} />
              <Route path="/admin/credits" element={<PrivateRoute><Layout><CreditConfigurationPage /></Layout></PrivateRoute>} />
              <Route path="/admin/emulation-audit" element={<PrivateRoute><Layout><EmulationAuditPage /></Layout></PrivateRoute>} />
              {/* Consolidated admin pages */}
              <Route path="/administration" element={<PrivateRoute><Layout><TenantAdministrationPage /></Layout></PrivateRoute>} />
              <Route path="/platform-settings" element={<PrivateRoute><Layout><PlatformSettingsPage /></Layout></PrivateRoute>} />
              <Route path="/support-tickets" element={<PrivateRoute><Layout><SupportTicketsPage /></Layout></PrivateRoute>} />
              <Route path="/billing" element={<PrivateRoute><Layout><BillingPage /></Layout></PrivateRoute>} />
              {/* Import wizard routes */}
              <Route path="/admin/imports" element={<PrivateRoute><Layout><ImportHistoryPage /></Layout></PrivateRoute>} />
              <Route path="/admin/imports/new" element={<PrivateRoute><Layout><ImportWizardPage /></Layout></PrivateRoute>} />
              <Route path="/admin/imports/:importId" element={<PrivateRoute><Layout><ImportWizardPage /></Layout></PrivateRoute>} />
              <Route path="/admin/imports/:importId/fix" element={<PrivateRoute><Layout><FixFailedRecordsPage /></Layout></PrivateRoute>} />
              {/* Collaborative submission view - outside Layout for cleaner form-like experience */}
              <Route path="/submissions/:formId/:submissionId" element={<PrivateRoute><CollaborativeSubmissionPage /></PrivateRoute>} />

              {/* Error pages */}
              <Route path="/forbidden" element={<ForbiddenPage />} />
              <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
                </ChunkErrorBoundary>
              </BrowserRouter>
              </InactivityLogoutWrapper>
            </LanguageProvider>
          </EmulationProvider>
        </MaintenanceProvider>
        </BrandingProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
